import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
} from "@mui/material";
import Colors from "../../../assets/Variables/ColorsVariables";
import * as Styles from "../Accounts/Styles.js";
import SearchBoxComponent from "../../Generals/SearchBox/SearchBoxComponent";
import Button3px from "../../Generals/Buttons/3pxButton";
import NotificationModal from "../../Generals/Notifications/NotificationModal";
import StatementRequestModal from "../../Generals/Modals/StatementsRequestModal";
import TapComponent from "../../Generals/TabPanels/TapComponent";
import CustomTapPanelComponent from "../../Generals/TabPanels/CustomTapPanelComponent";
import TableFrontPagination from "../../Generals/Tables/TableFrontPagination";
import IconComponent from "../../Generals/Icons/IconComponent";
import TitledSelectorSlim from "../../Generals/Selectors/TitledSelectorSlim";
import DateSelector from "../../Generals/Selectors/DateSelector";
import StatusChip from "../../Generals/Chips/StatusChip";

// Options period of time
const DATE_OPTIONS = [
  { label: "Today", value: "Today" },
  { label: "This week", value: "This week" },
  { label: "This month", value: "This month" },
  { label: "This year", value: "This year" },
  { label: "Custom Period", value: "Custom" },
];

// Status chips
const StatementStatusChip = ({ value }) => <StatusChip value={value} />;

// Columns
const STATEMENTS_COLUMNS = [
  { field: "request_id", headerName: "Request ID", flex: 2 },
  { field: "submitted_on", headerName: "Submitted on", flex: 2 },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    renderCell: (params) => <StatementStatusChip value={params.value} />,
  },
  { field: "signal_number", headerName: "Signal Number", flex: 1 },
  { field: "strategy_name", headerName: "Strategy Name", flex: 2 },
  {
    field: "assignee",
    headerName: "Assignee",
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "left",
          alignItems: "center",
          height: "100%",
          py: "8px",
        }}
      >
        <Button
          variant="outlined"
          sx={{
            ...Styles.ExportButton,
            minHeight: "32px",
            height: "32px",
            px: 2,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            whiteSpace: "nowrap",
          }}
          onClick={() => params.row?.onMoreDetails?.(params.row)}
        >
          <Typography
            sx={{
              fontFamily: "Plus Jakarta Sans, sans-serif",
              fontWeight: 700,
              fontSize: "12px",
              lineHeight: 1.4,
              textAlign: "center",
              margin: 0,
            }}
          >
            More Details
          </Typography>
        </Button>
      </Box>
    ),
  },
];

const StatementsScreen = () => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    waiting: 0,
    in_progress: 0,
    rejected: 0,
    resolved: 0,
  });
  const [tabstate, setTabstate] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateSelect, setDateSelect] = useState("Today");
  const [showDatePickers, setShowDatePickers] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [allStatementsRows, setAllStatementsRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);

  const [modalConfig, setModalConfig] = useState({
    open: false,
    iconType: "info",
    title: "",
    message: "",
    onAcceptLabel: "OK",
    onRefuseLabel: undefined,
    onContinueLabel: undefined,
    onAccept: undefined,
    onRefuse: undefined,
    onContinue: undefined,
    onClose: () => setModalConfig((prev) => ({ ...prev, open: false })),
  });

  const [statementModalConfig, setStatementModalConfig] = useState({
    open: false,
    title: "",
    fields: [],
    onSubmit: null,
    onClose: () => setStatementModalConfig((prev) => ({ ...prev, open: false })),
  });

  const openSuccessModal = (afterClose) =>
    setModalConfig({
      open: true,
      iconType: "success",
      title: "The operation was successful",
      message: "",
      onAcceptLabel: undefined,
      onRefuseLabel: undefined,
      onContinueLabel: "Continue",
      onAccept: undefined,
      onRefuse: undefined,
      onContinue: () => {
        if (afterClose) afterClose();
        setModalConfig((prev) => ({ ...prev, open: false }));
      },
      onClose: () =>
        setModalConfig((prev) => ({
          ...prev,
          open: false,
        })),
    });

  const openErrorModal = (message) =>
    setModalConfig({
      open: true,
      iconType: "error",
      title: message || "Server error",
      message: "",
      onAcceptLabel: "OK",
      onRefuseLabel: undefined,
      onContinueLabel: undefined,
      onAccept: () =>
        setModalConfig((prev) => ({
          ...prev,
          open: false,
        })),
      onRefuse: undefined,
      onContinue: undefined,
      onClose: () =>
        setModalConfig((prev) => ({
          ...prev,
          open: false,
        })),
    });

  // MOCK: summary data, delete when connecting to backend
  const RequestSummary = () => {
    setSummary({
      waiting: 1,
      in_progress: 2,
      rejected: 1,
      resolved: 3,
    });
  };

  const buildFilters = () => {
    let periodFilter = {
      period: dateSelect,
      from: null,
      to: null,
    };

    if (dateSelect === "Custom") {
      periodFilter.period = "Custom";
      periodFilter.from = fromDate;
      periodFilter.to = toDate;
    }

    return {
      search: searchTerm,
      ...periodFilter,
    };
  };

  // MOCK: data of statements, delete when connecting to backend
  const RequestStatements = () => {
    setLoading(true);
    const filters = buildFilters();
    console.log("Filters simulados:", filters);

    const mockData = [
      {
        request_id: "REQ-001",
        submitted_on: "02/12/2024",
        status: "Waiting",
        signal_number: "123456789",
        strategy_name: "Estrategia Free",
        assignee: "Admin 1",
        requested_by: "123456789",
        signal_name: "Pepito Perez",
        signal_strategy: "La mas mejorcita",
        attached_file: "exness_trading_mt5.html",
        external_broker: "Exness",
        platform_type: "Metatrader 5",
        external_login: "123456789",
        external_password: "Passwordcta",
        additional_comments: "Example example 122",
        reason_feedback: "",
      },
      {
        request_id: "REQ-002",
        submitted_on: "02/12/2024",
        status: "InProgress",
        signal_number: "123456789",
        strategy_name: "Estrategia Free",
        assignee: "Admin 2",
        requested_by: "123456789",
        signal_name: "Pepito Perez",
        signal_strategy: "La mas mejorcita",
        attached_file: "exness_trading_mt5.html",
        external_broker: "Exness",
        platform_type: "Metatrader 5",
        external_login: "123456789",
        external_password: "Passwordcta",
        additional_comments: "Second pending",
        reason_feedback: "",
      },
      {
        request_id: "REQ-003",
        submitted_on: "02/12/2024",
        status: "Resolved",
        signal_number: "123456789",
        strategy_name: "Estrategia Free",
        assignee: "Admin 3",
        requested_by: "123456789",
        signal_name: "Pepito Perez",
        signal_strategy: "La mas mejorcita",
        attached_file: "exness_trading_mt5.html",
        external_broker: "Exness",
        platform_type: "Metatrader 5",
        external_login: "123456789",
        external_password: "Passwordcta",
        additional_comments: "Resolved ok",
        reason_feedback: "Everything is fine",
      },
      {
        request_id: "REQ-004",
        submitted_on: "02/12/2024",
        status: "Rejected",
        signal_number: "123456789",
        strategy_name: "Estrategia Free",
        assignee: "Admin 4",
        requested_by: "123456789",
        signal_name: "Pepito Perez",
        signal_strategy: "La mas mejorcita",
        attached_file: "exness_trading_mt5.html",
        external_broker: "Exness",
        platform_type: "Metatrader 5",
        external_login: "123456789",
        external_password: "Passwordcta",
        additional_comments: "Rejected for test",
        reason_feedback: "Wrong file format",
      },
    ];

    const rows = mockData.map((item, index) => ({
      id: index,
      request_id: item.request_id,
      submitted_on: item.submitted_on,
      status: item.status,
      signal_number: item.signal_number,
      strategy_name: item.strategy_name,
      assignee: item.assignee,
      raw: item,
      onMoreDetails: handleOpenDetailsModal,
    }));

    setAllStatementsRows(rows);
    setLoading(false);
  };

  const rowsPending = allStatementsRows.filter(
    (row) => row.status === "Waiting" || row.status === "InProgress"
  );

  const rowsHistory = allStatementsRows.filter(
    (row) => row.status === "Rejected" || row.status === "Resolved"
  );

  useEffect(() => {
    RequestSummary();
    RequestStatements();
  }, []);

  const handleChangeTab = (newTab) => {
    setTabstate(newTab);
  };

  const handleDateSelector = (event) => {
    const value = event.target.value;
    setDateSelect(value);
    if (value === "Custom") {
      setShowDatePickers(true);
    } else {
      setShowDatePickers(false);
    }
  };

  const handleSearchClick = () => {
    RequestStatements();
  };

  const handleOpenDetailsModal = (row) => {
    const data = row.raw || row;

    const fields = [
      {
        name: "request_id",
        label: "Request ID",
        value: data.request_id,
      },
      {
        name: "status",
        label: "Status",
        type: "select",
        value: data.status || "Waiting",
        options: [
          { value: "Waiting", label: "Waiting" },
          { value: "InProgress", label: "In Progress" },
          { value: "Rejected", label: "Rejected" },
          { value: "Resolved", label: "Resolved" },
        ],
      },
      {
        name: "submitted_on",
        label: "Submitted on",
        value: data.submitted_on,
      },
      {
        name: "requested_by",
        label: "Requested by",
        value: data.requested_by || data.signal_number || "",
      },
      {
        name: "signal_name",
        label: "Signal Name",
        value: data.signal_name || "",
      },
      {
        name: "signal_strategy",
        label: "Signal Strategy",
        value: data.signal_strategy || data.strategy_name || "",
      },
      {
        name: "signal_login",
        label: "Signal Login",
        value: data.signal_number || "",
      },
      {
        name: "attached_file",
        label: "Attached File",
        value: data.attached_file || "",
      },
      {
        name: "external_broker",
        label: "External Broker",
        value: data.external_broker || "",
      },
      {
        name: "platform_type",
        label: "Platform Type",
        value: data.platform_type || "",
      },
      {
        name: "external_login",
        label: "External Login",
        value: data.external_login || "",
      },
      {
        name: "external_password",
        label: "External Password",
        value: data.external_password || "",
      },
      {
        name: "additional_comments",
        label: "Additional Comments",
        value: data.additional_comments || "",
      },
      {
        name: "reason_feedback",
        label: "Reason (Feedback)",
        value: data.reason_feedback || "",
      },
    ];

    setStatementModalConfig({
      open: true,
      title: "Statement Request",
      fields,
      onSubmit: handleSubmitDetails(data),
      onClose: () =>
        setStatementModalConfig((prev) => ({ ...prev, open: false })),
    });
  };

  // MOCK: act memory
  const handleSubmitDetails = (rowData) => async (formData) => {
    try {
      const newStatus = formData.status || rowData.status;
      const newReason = formData.reason_feedback || rowData.reason_feedback;

      const updated = allStatementsRows.map((row) =>
        row.request_id === rowData.request_id
          ? {
            ...row,
            status: newStatus,
            raw: {
              ...row.raw,
              status: newStatus,
              reason_feedback: newReason,
            },
          }
          : row
      );

      setAllStatementsRows(updated);
      setStatementModalConfig((prev) => ({ ...prev, open: false }));
      openSuccessModal();
    } catch (e) {
      console.error(e);
      openErrorModal("Failed to update statement (mock).");
    }
  };

  const renderSummaryCards = () => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
      <Paper
        elevation={1}
        sx={{
          flex: "1 1 200px",
          p: 2,
          borderRadius: "12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "14px", color: Colors.Secondary_700 }}>
            Waiting
          </Typography>
          <Typography sx={{ fontSize: "24px", fontWeight: 700 }}>
            {summary.waiting}
          </Typography>
        </Box>
        <IconComponent icon="error_outline" size="24px" />
      </Paper>

      <Paper
        elevation={1}
        sx={{
          flex: "1 1 200px",
          p: 2,
          borderRadius: "12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "14px", color: Colors.Secondary_700 }}>
            In progress
          </Typography>
          <Typography sx={{ fontSize: "24px", fontWeight: 700 }}>
            {summary.in_progress}
          </Typography>
        </Box>
        <IconComponent icon="schedule" size="24px" />
      </Paper>

      <Paper
        elevation={1}
        sx={{
          flex: "1 1 200px",
          p: 2,
          borderRadius: "12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "14px", color: Colors.Secondary_700 }}>
            Rejected
          </Typography>
          <Typography sx={{ fontSize: "24px", fontWeight: 700 }}>
            {summary.rejected}
          </Typography>
        </Box>
        <IconComponent icon="close" size="24px" />
      </Paper>

      <Paper
        elevation={1}
        sx={{
          flex: "1 1 200px",
          p: 2,
          borderRadius: "12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "14px", color: Colors.Secondary_700 }}>
            Resolved
          </Typography>
          <Typography sx={{ fontSize: "24px", fontWeight: 700 }}>
            {summary.resolved}
          </Typography>
        </Box>
        <IconComponent icon="check_circle" size="24px" />
      </Paper>
    </Box>
  );

  return (
    <Paper elevation={2} sx={Styles.containerPaper}>
      <NotificationModal {...modalConfig} />

      {/* New Modal Statements Request */}
      <StatementRequestModal
        open={statementModalConfig.open}
        title={statementModalConfig.title}
        fields={statementModalConfig.fields}
        onSubmit={statementModalConfig.onSubmit}
        onClose={statementModalConfig.onClose}
        confirmLabel="Save Changes"
        cancelLabel="Cancel"
      />

      <Typography variant="h5" gutterBottom>
        Statements
      </Typography>
      <Typography sx={{ fontSize: "15px", color: "#687B8E", mb: 2 }}>
        Check here for pending, ongoing, or resolved requests for signal
        providers statements.
      </Typography>

      {renderSummaryCards()}

      <TapComponent
        onTabstate={handleChangeTab}
        tapsContent={["Pending", "History"]}
      />

      {/* TAB PENDING */}
      <CustomTapPanelComponent value={tabstate} index={0}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            mb: 3,
            p: 2,
            bgcolor: "#F8FAFC",
            borderRadius: "10px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                fontFamily: "Plus Jakarta Sans, sans-serif",
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: 2.5,
                color: Colors.Secondary_700,
                marginTop: "-10px",
              }}
            >
              Search
            </Typography>
            <SearchBoxComponent
              value={searchTerm}
              handleSearchTerm={(e) => setSearchTerm(e.target.value)}
              placeholderprops={"Search for id, login, name or strategy"}
              variant="statements"
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography sx={{
              fontFamily: "Plus Jakarta Sans, sans-serif",
              fontWeight: 400,
              fontSize: "11px",
              lineHeight: "140%",
              color: Colors.Secondary_700,
            }}>
              Period of time
            </Typography>
            <TitledSelectorSlim
              title=""
              value={dateSelect}
              handleChange={handleDateSelector}
              options={DATE_OPTIONS}
              variant="statements"
            />
          </Box>

          {showDatePickers && (
            <>
              <DateSelector
                title="From"
                value={fromDate}
                handleChange={setFromDate}
                variant="statements"
              />
              <DateSelector
                title="To"
                value={toDate}
                handleChange={setToDate}
                variant="statements"
              />
            </>
          )}
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            alignSelf: "flex-end",
            ml: "left"
          }}>
            <Button3px
              label={"Search"}
              color={Colors.Warning_50}
              backgroundColor={Colors.Error_500}
              type="submit"
              HandleOnClick={handleSearchClick}
              height="40px"
            >
              Search
            </Button3px>
          </Box>

        </Box>

        <Box style={{ height: 450, width: "100%", marginTop: "0px" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <TableFrontPagination
              rows={rowsPending}
              columns={STATEMENTS_COLUMNS}
            />
          )}
        </Box>
      </CustomTapPanelComponent>

      {/* TAB HISTORY */}
      <CustomTapPanelComponent value={tabstate} index={1}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            mb: 3,
            p: 2,
            bgcolor: "#F8FAFC",
            borderRadius: "10px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                fontFamily: "Plus Jakarta Sans, sans-serif",
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: 2.5,
                color: Colors.Secondary_700,
                marginTop: "-10px",
              }}
            >
              Search
            </Typography>
            <SearchBoxComponent
              value={searchTerm}
              handleSearchTerm={(e) => setSearchTerm(e.target.value)}
              placeholderprops={"Search for id, login, name or strategy"}
              variant="statements"
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography sx={{
              fontFamily: "Plus Jakarta Sans, sans-serif",
              fontWeight: 400,
              fontSize: "11px",
              lineHeight: "140%",
              color: Colors.Secondary_700,
            }}>
              Period of time
            </Typography>
            <TitledSelectorSlim
              title=""
              value={dateSelect}
              handleChange={handleDateSelector}
              options={DATE_OPTIONS}
              variant="statements"
            />
          </Box>
          {showDatePickers && (
            <>
              <DateSelector
                title="From"
                value={fromDate}
                handleChange={setFromDate}
                variant="statements"
              />
              <DateSelector
                title="To"
                value={toDate}
                handleChange={setToDate}
                variant="statements"
              />
            </>
          )}

          <Box sx={{
            display: "flex",
            flexDirection: "column",
            alignSelf: "flex-end",
            ml: "left"
          }}>
            <Button3px
              label={"Search"}
              color={Colors.Warning_50}
              backgroundColor={Colors.Error_500}
              type="submit"
              HandleOnClick={handleSearchClick}
              height="40px"
            >
              Search
            </Button3px>
          </Box>

        </Box>

        <Box style={{ height: 450, width: "100%", marginTop: "0px" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <TableFrontPagination
              rows={rowsHistory}
              columns={STATEMENTS_COLUMNS}
            />
          )}
        </Box>
      </CustomTapPanelComponent>
    </Paper>
  );
};

export default StatementsScreen;